import { useCallback, useEffect, useState } from 'react'

export const THEME_STORAGE_KEY = document.documentElement.dataset.themeStorageKey || 'expandrive-teams-theme'
export const LIGHT_THEME = 'light'
export const DARK_THEME = 'dark'
export const THEME_CHANGE_EVENT = 'expandrive-theme-change'

const isTheme = theme => theme === LIGHT_THEME || theme === DARK_THEME
let transitionTimer

export const getThemePreference = () => {
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (isTheme(storedTheme)) return storedTheme
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  return document.documentElement.dataset.theme === DARK_THEME ? DARK_THEME : LIGHT_THEME
}

export const applyTheme = (theme, { animate = false } = {}) => {
  const resolvedTheme = isTheme(theme) ? theme : LIGHT_THEME
  const root = document.documentElement
  const previousTheme = root.dataset.theme

  if (animate) {
    root.classList.add('ds-theme-transition')
    window.clearTimeout(transitionTimer)
    transitionTimer = window.setTimeout(() => root.classList.remove('ds-theme-transition'), 220)
  }

  root.dataset.theme = resolvedTheme
  root.style.colorScheme = resolvedTheme

  const themeColor = document.querySelector('meta[name="theme-color"]')
  const themeColorValue = themeColor?.dataset?.[resolvedTheme]
  if (themeColorValue) themeColor.setAttribute('content', themeColorValue)

  if (previousTheme !== resolvedTheme) {
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { theme: resolvedTheme } }))
  }

  return resolvedTheme
}

export const storeThemePreference = theme => {
  const resolvedTheme = isTheme(theme) ? theme : LIGHT_THEME

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, resolvedTheme)
  } catch {
    // Applying the theme still works even when persistence is unavailable.
  }

  return resolvedTheme
}

export const useTheme = () => {
  const [theme, setThemeState] = useState(getThemePreference)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    const syncThemeAcrossTabs = event => {
      if (event.key !== THEME_STORAGE_KEY || !isTheme(event.newValue)) return
      setThemeState(event.newValue)
    }

    window.addEventListener('storage', syncThemeAcrossTabs)
    return () => window.removeEventListener('storage', syncThemeAcrossTabs)
  }, [])

  useEffect(() => {
    const syncThemeInCurrentTab = event => {
      if (!isTheme(event.detail?.theme)) return
      setThemeState(event.detail.theme)
    }

    window.addEventListener(THEME_CHANGE_EVENT, syncThemeInCurrentTab)
    return () => window.removeEventListener(THEME_CHANGE_EVENT, syncThemeInCurrentTab)
  }, [])

  const setTheme = useCallback(nextTheme => {
    const resolvedTheme = storeThemePreference(nextTheme)
    applyTheme(resolvedTheme, { animate: true })
    setThemeState(resolvedTheme)
  }, [])

  const isDark = theme === DARK_THEME

  return {
    isDark,
    setTheme,
    theme,
    toggleTheme: () => setTheme(isDark ? LIGHT_THEME : DARK_THEME),
  }
}
